import React, { useState } from "react";
import "./CustomTable.css";

interface Column {
    id: string;
    label: string;
    minWidth?: number;
    align?: "right" | "left" | "center";
    sortable?: boolean; // ✅ enable header click for this column
}

interface CustomTableSortableProps {
    columns: Column[];
    rows: Record<string, any>[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onRowsReorder?: (newRows: Record<string, any>[]) => void;
    draggable?: boolean;

    // ✅ sorting props
    sortField?: string; // column.id to highlight arrow
    sortOrder?: "asc" | "desc";
    onSortChange?: (field: string, order: "asc" | "desc") => void;

    // ✅ optional loading state
    isLoading?: boolean;
     pagination?: boolean; 
}

const getAlignClass = (align?: "right" | "left" | "center") => {
    switch (align) {
        case "right":
            return "text-end";
        case "center":
            return "text-center";
        default:
            return "text-start";
    }
};

const CustomTableSortable: React.FC<CustomTableSortableProps> = ({
    columns,
    rows,
    totalCount,
    currentPage,
    pageSize,
    onPageChange,
    onRowsReorder,
    draggable = false,
    sortField,
    sortOrder = "asc",
    onSortChange,
    isLoading = false,
    pagination = true,
}) => {
    const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
    const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
    const totalPages = Math.ceil(totalCount / pageSize) || 1;

   const handleAutoScroll = (e: React.DragEvent) => {
  const scrollThreshold = 120;

  const clientY = e.clientY;
  const windowHeight = window.innerHeight;

  // 🔼 Distance from TOP
  const topDistance = clientY;

  // 🔽 Distance from BOTTOM
  const bottomDistance = windowHeight - clientY;

  let scrollSpeed = 0;

  // ⬆️ SCROLL UP (faster when closer to top)
  if (topDistance < scrollThreshold) {
    scrollSpeed = Math.max(5, (scrollThreshold - topDistance) / 2);
    window.scrollBy({ top: -scrollSpeed, behavior: "auto" });
  }

  // ⬇️ SCROLL DOWN (same logic)
  else if (bottomDistance < scrollThreshold) {
    scrollSpeed = Math.max(5, (scrollThreshold - bottomDistance) / 2);
    window.scrollBy({ top: scrollSpeed, behavior: "auto" });
  }
};

    const handleDragStart = (idx: number, e: React.DragEvent) => {
        setDraggedIdx(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", idx.toString());
    };

    const handleDragOver = (idx: number, e: React.DragEvent) => {
        e.preventDefault();
        setDragOverIdx(idx);

        handleAutoScroll(e);
    };

    const handleDrop = (idx: number, e: React.DragEvent) => {
        e.preventDefault();
        const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);

        if (draggedIndex !== idx && onRowsReorder) {
            const newRows = [...rows];
            const [removed] = newRows.splice(draggedIndex, 1);
            newRows.splice(idx, 0, removed);
            onRowsReorder(newRows);
        }

        setDraggedIdx(null);
        setDragOverIdx(null);
    };

    const handleDragEnd = () => {
        setDraggedIdx(null);
        setDragOverIdx(null);
    };

    const handleHeaderClick = (columnId: string, sortable?: boolean) => {
        if (!sortable || !onSortChange) return;

        let nextOrder: "asc" | "desc" = "asc";

        if (sortField === columnId) {
            nextOrder = sortOrder === "asc" ? "desc" : "asc";
        } else {
            nextOrder = "asc";
        }

        onSortChange(columnId, nextOrder);
    };

    const renderSortIcon = (columnId: string) => {
        if (sortField !== columnId) return null;
        return (
            <span className="ms-1 sort-icon">
                {sortOrder === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    return (
        <div className="table-container">
            <table className="align-middle custom-table">
                <thead className="table-dark">
                    <tr>
                        {columns.map((column) => {
                            const sortable = column.sortable && !!onSortChange;
                            return (
                                <th
                                    key={column.id}
                                    className={`${getAlignClass(column.align)} ${
                                        sortable ? "sortable-header" : ""
                                    }`}
                                    style={{ minWidth: column.minWidth || 100, cursor: sortable ? "pointer" : "default" }}
                                    onClick={() => handleHeaderClick(column.id, sortable)}
                                >
                                    <span className="d-inline-flex align-items-center">
                                        {column.label}
                                        {renderSortIcon(column.id)}
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                Loading...
                            </td>
                        </tr>
                    ) : rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                No data available.
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, idx) => (
                            <tr
                                key={idx}
                                draggable={draggable}
                                onDragStart={draggable ? (e) => handleDragStart(idx, e) : undefined}
                                onDragOver={draggable ? (e) => handleDragOver(idx, e) : undefined}
                                onDrop={draggable ? (e) => handleDrop(idx, e) : undefined}
                                onDragEnd={draggable ? handleDragEnd : undefined}
                                className={
                                    draggable && draggedIdx === idx
                                        ? "dragging-row"
                                        : draggable && dragOverIdx === idx
                                        ? "drag-over-row"
                                        : "table-row"
                                }
                            >
                                {columns.map((column) => (
                                    <td
                                        key={column.id}
                                        className={getAlignClass(column.align)}
                                    >
                                        {row[column.id]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            {pagination && (
            <div className="pagination-container">
                <button
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    className="page-btn"
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    className="page-btn"
                >
                    Next
                </button>
            </div>
            )}
        </div>
    );
};

export default CustomTableSortable;
